import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "./api";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [isLoggedIn, setIsLoggedIn] = useState(!!token);
  const [userId, setUserId] = useState(localStorage.getItem("userId") || "");
  const [userName, setUserName] = useState(localStorage.getItem("userName") || "");
  const [baseUsername, setbaseUserName] = useState(localStorage.getItem("baseUsername") || "");


  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("userName");
    localStorage.removeItem("baseUsername");
    localStorage.removeItem("kycVerified");

    setUser(null);
    setToken("");
    setIsLoggedIn(false);
    setUserId("");
    setUserName("");

    navigate("/login");
  }, [navigate]);

  // Fetch full user profile from backend (including KYC status)
  const fetchUserProfile = useCallback(async (uid, userToken) => {
    const targetUserId = uid || userId || localStorage.getItem("userId");
    if (!targetUserId) return null;
    try {
      const activeToken = userToken || token || localStorage.getItem("token");
      const headers = activeToken ? { Authorization: `Bearer ${activeToken}` } : {};
      const res = await axios.get(`${API_BASE_URL}/api/auth/user/${targetUserId}`, { headers });
      if (res.data) {
        const isVerified = Boolean(
          res.data.kycVerified ||
          res.data.kycStatus === 'VERIFIED' ||
          res.data.kyc?.aadhar?.verified ||
          res.data.kyc?.pan?.verified ||
          res.data.kyc?.drivingLicence?.verified
        );

        if (isVerified) {
          localStorage.setItem("kycVerified", "true");
        } else {
          localStorage.removeItem("kycVerified");
        }

        setUser((prevUser) => ({
          ...prevUser,
          ...res.data,
          userId: res.data._id || targetUserId,
          kycVerified: isVerified,
          kycStatus: isVerified ? 'VERIFIED' : (res.data.kycStatus || 'NOT_VERIFIED')
        }));
        return res.data;
      }
    } catch (err) {
      console.error("Failed to fetch user profile:", err.message);
    }
  }, [token, userId]);

  const refreshUser = useCallback(async () => {
    const currentUserId = user?.userId || user?._id || userId || localStorage.getItem("userId");
    if (currentUserId) {
      return await fetchUserProfile(currentUserId);
    }
  }, [user, userId, fetchUserProfile]);

  // Token validation & auto-login
  useEffect(() => {
    if (token) {
      try {
        const decodedUser = jwtDecode(token);

        // Check token expiration
        if (decodedUser.exp * 1000 < Date.now()) {
          console.warn("Token expired, logging out.");
          logout();
        } else {
          const uId = decodedUser.userId || decodedUser.id;
          const isVerified = Boolean(
            decodedUser.kycVerified ||
            localStorage.getItem("kycVerified") === "true"
          );

          setUser((prev) => ({
            ...decodedUser,
            userId: uId,
            kycVerified: isVerified,
            ...(prev || {})
          }));
          setIsLoggedIn(true);
          setUserId(uId);
          setUserName(decodedUser.userName);
          fetchUserProfile(uId, token);
        }
      } catch (error) {
        console.error("Invalid token:", error);
        logout();
      }
    } else {
      setIsLoggedIn(false);
      setUser(null);
    }
  }, [token, logout, fetchUserProfile]);

  // Handle Google Login
  useEffect(() => {
    const handleGoogleLogin = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const googleToken = urlParams.get("token");

      if (googleToken) {
        try {
          const decodedUser = jwtDecode(googleToken);

          if (decodedUser.exp * 1000 < Date.now()) {
            console.warn("Google token expired");
            logout();
            return;
          }

          const isVerified = Boolean(decodedUser.kycVerified);

          localStorage.setItem("token", googleToken);
          localStorage.setItem("userId", decodedUser.userId);
          localStorage.setItem("userName", decodedUser.userName);
          localStorage.setItem("baseUsername", decodedUser.baseUsername);
          if (isVerified) {
            localStorage.setItem("kycVerified", "true");
          }

          setToken(googleToken);
          setUser({ ...decodedUser, kycVerified: isVerified });
          setIsLoggedIn(true);
          setUserId(decodedUser.userId);
          setUserName(decodedUser.userName);
          setbaseUserName(decodedUser.baseUsername);

          await fetchUserProfile(decodedUser.userId, googleToken);

          window.history.replaceState(null, null, window.location.pathname);
          navigate("/UserDashBoard");
        } catch (error) {
          console.error("Google login error:", error);
          logout();
        }
      }
    };

    handleGoogleLogin();
  }, [navigate, logout, fetchUserProfile]);

  // Signup function
  const signup = async (userData) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/signup`, userData);
      navigate("/login");
    } catch (error) {
      console.error("Signup error:", error.response?.data || error.message);
      alert(error.response?.data?.message || "Signup failed");
    }
  };

  // Login function
  const login = async (credentials) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/login`, credentials);
      
      const token = res.data?.data?.token || res.data?.token;

      if (!token) throw new Error("No token received. Response: " + JSON.stringify(res.data));

      const decodedUser = jwtDecode(token);
      if (!decodedUser?.userId || !decodedUser?.userName) throw new Error("Invalid token data");

      const isVerified = Boolean(
        decodedUser.kycVerified ||
        res.data?.user?.kycVerified ||
        res.data?.user?.kycStatus === 'VERIFIED' ||
        res.data?.user?.kyc?.aadhar?.verified ||
        res.data?.user?.kyc?.pan?.verified ||
        res.data?.user?.kyc?.drivingLicence?.verified
      );

      localStorage.setItem("token", token);
      localStorage.setItem("userId", decodedUser.userId);
      localStorage.setItem("userName", decodedUser.userName);
      localStorage.setItem("baseUsername", decodedUser.baseUsername || "");
      if (isVerified) {
        localStorage.setItem("kycVerified", "true");
      } else {
        localStorage.removeItem("kycVerified");
      }

      setToken(token);
      setUser({
        ...decodedUser,
        ...(res.data?.user || {}),
        kycVerified: isVerified,
        kycStatus: isVerified ? 'VERIFIED' : 'NOT_VERIFIED'
      });
      setIsLoggedIn(true);
      setUserId(decodedUser.userId);
      setUserName(decodedUser.userName);
      setbaseUserName(decodedUser.baseUsername);

      await fetchUserProfile(decodedUser.userId, token);

      navigate("/UserDashboard");
    } catch (error) {
      console.log("Login error:", error);
      if (error.response?.status === 401) {
        alert("Invalid credentials, please try again.");
      } else if (error.response?.status === 404) {
        alert("User not found, please sign up.");
      }
      alert(error.response?.data?.message || "Login failed");
    }
  };

  // Google Login function
  const googleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/auth/google`;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        setIsLoggedIn,
        logout,
        setUser,
        googleLogin,
        signup,
        login,
        userId,
        userName,
        baseUsername,
        refreshUser,
        fetchUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};